/**
 * Google Identity Services ("Sign in with Google"): loads Google's script once and renders its button.
 * The callback receives a Google-signed ID token, which the API verifies; the browser never needs a client secret.
 */
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface GoogleCredentialResponse {
   credential?: string;
}

interface GoogleAccountsId {
   initialize(config: { client_id: string; callback: (response: GoogleCredentialResponse) => void; ux_mode?: 'popup' | 'redirect'; auto_select?: boolean; cancel_on_tap_outside?: boolean }): void;
   renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
}

declare global {
   interface Window {
      google?: { accounts?: { id?: GoogleAccountsId } };
   }
}

let scriptPromise: Promise<GoogleAccountsId> | null = null;

export const loadGoogleIdentity = (): Promise<GoogleAccountsId> => {
   if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
   if (scriptPromise) return scriptPromise;

   scriptPromise = new Promise<GoogleAccountsId>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => (window.google?.accounts?.id ? resolve(window.google.accounts.id) : reject(new Error('Google Identity Services did not load')));
      script.onerror = () => {
         scriptPromise = null;
         reject(new Error('Google Identity Services could not be loaded'));
      };
      document.head.appendChild(script);
   });
   return scriptPromise;
};

// Google allows one initialize() per page; the callback always goes to the button rendered last.
let initializedClientId: string | null = null;
let currentOnIdToken: ((idToken: string) => void) | null = null;

/**
 * Renders the "Continue with Google" button into `parent`, once; `onIdToken` gets the Google-signed ID token.
 * Always Google's outline (white pill) variant: it reads on both of the chat's themes, and drawing it only once
 * means a theme switch never redraws it.
 */
export const renderGoogleSignInButton = async (parent: HTMLElement, clientId: string, onIdToken: (idToken: string) => void, width: number): Promise<void> => {
   const googleId = await loadGoogleIdentity();
   currentOnIdToken = onIdToken;
   if (initializedClientId !== clientId) {
      googleId.initialize({
         client_id: clientId,
         ux_mode: 'popup',
         auto_select: false,
         cancel_on_tap_outside: true,
         callback: (response) => {
            if (response.credential) currentOnIdToken?.(response.credential);
         }
      });
      initializedClientId = clientId;
   }
   googleId.renderButton(parent, { type: 'standard', theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', logo_alignment: 'left', width });
};
